import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  useActions,
  useArAging,
  useDailyBankBalances,
  useFacility,
  useForecast,
} from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt, STATUS_BAR_COLORS } from './utils'
import type { Action, ArAgingRow, FacilityConfig, WeeklyForecastRow } from './types'

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

function CovenantGauge({ ratio, label }: { ratio: number; label: string }) {
  const clamped = Math.max(0, Math.min(1, ratio))
  const radius = 70
  const stroke = 14
  const cx = 90
  const cy = 85
  const circumference = Math.PI * radius
  const filled = circumference * clamped

  const gaugeColor =
    clamped >= 0.6
      ? colors.status.green
      : clamped >= 0.3
        ? colors.status.yellow
        : colors.status.red

  return (
    <div className="flex flex-col items-center">
      <svg width="180" height="100" viewBox="0 0 180 100">
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={stroke}
          strokeLinecap="round"
        />
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke={gaugeColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
        />
        <text
          x={cx}
          y={cy - 10}
          textAnchor="middle"
          className="text-lg font-bold"
          fill={colors.text.primary}
        >
          {fmt.pct(ratio * 100)}
        </text>
      </svg>
      <span className="text-xs text-gray-500 dark:text-gray-400 -mt-1">
        {label}
      </span>
    </div>
  )
}

function WeekTooltip({ active, payload }: any) {
  if (!active || !payload?.[0]) return null
  const data = payload[0].payload as WeeklyForecastRow
  return (
    <div className="rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-lg">
      <p className="font-semibold mb-1">Week {data.week}</p>
      <p>Ending Cash: {fmt.currency(data.ending_cash)}</p>
      <p>
        Status:{' '}
        <span
          style={{ color: STATUS_BAR_COLORS[data.liquidity_status] ?? '#ffffff' }}
        >
          {data.liquidity_status}
        </span>
      </p>
    </div>
  )
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: colors.status.red,
  high: colors.status.yellow,
  medium: colors.status.blue,
  low: colors.status.green,
}

interface EntitySummary {
  name: string
  total: number
  over90: number
  over90Pct: number
}

export default function LiquidityPulsePage() {
  const forecast = useForecast()
  const facility = useFacility()
  const actions = useActions()
  const dailyBank = useDailyBankBalances(400)
  const arAging = useArAging()

  const rows: WeeklyForecastRow[] = useMemo(() => forecast.data ?? [], [forecast.data])
  const fac: FacilityConfig | undefined = facility.data
  const allActions: Action[] = useMemo(() => actions.data ?? [], [actions.data])
  const bankRows = useMemo(() => dailyBank.data ?? [], [dailyBank.data])
  const arRows: ArAgingRow[] = useMemo(() => arAging.data ?? [], [arAging.data])

  const weekOne = rows.find(row => Number(row.week) === 1)
  const weekThirteen = rows.find(row => Number(row.week) === 13)

  const totalNetCashFlow = useMemo(
    () => rows.reduce((sum, row) => sum + row.net_cash_flow, 0),
    [rows],
  )

  const minHeadroom = useMemo(
    () => (rows.length ? Math.min(...rows.map(row => row.covenant_headroom)) : 0),
    [rows],
  )

  const entityHealth: EntitySummary[] = useMemo(() => {
    const grouped = new Map<string, { total: number; over90: number }>()
    for (const row of arRows) {
      const business = row.Business
      if (!grouped.has(business)) grouped.set(business, { total: 0, over90: 0 })
      const entry = grouped.get(business)!
      entry.total += row['Open Balance']
      if (row.Aging === '91+' || row.Aging === 'Over 90') {
        entry.over90 += row['Open Balance']
      }
    }
    return Array.from(grouped.entries())
      .map(([name, values]) => ({
        name,
        total: values.total,
        over90: values.over90,
        over90Pct: values.total > 0 ? values.over90 / values.total : 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
  }, [arRows])

  const priorityActions = useMemo(() => {
    const priorityOrder: Record<string, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
    }
    return allActions
      .filter(
        action => action.status !== 'resolved' && action.status !== 'cancelled',
      )
      .sort(
        (a, b) =>
          (priorityOrder[a.priority] ?? 9) - (priorityOrder[b.priority] ?? 9),
      )
      .slice(0, 6)
  }, [allActions])

  const recentBank = useMemo(
    () =>
      [...bankRows]
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(-60),
    [bankRows],
  )

  const revolverUtilPct = fac
    ? (fac.current_outstanding / fac.total_commitment) * 100
    : 0

  const isLoading =
    forecast.isLoading ||
    facility.isLoading ||
    actions.isLoading ||
    dailyBank.isLoading ||
    arAging.isLoading

  const firstError =
    forecast.error ||
    facility.error ||
    actions.error ||
    dailyBank.error ||
    arAging.error

  if (isLoading) {
    return <CashflowV2Loading label="Loading liquidity pulse…" />
  }

  if (firstError) {
    if (looksUnconfigured(firstError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load dashboard data"
        description={(firstError as Error).message}
        onRetry={() => {
          forecast.refetch()
          facility.refetch()
          actions.refetch()
          dailyBank.refetch()
          arAging.refetch()
        }}
      />
    )
  }

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Liquidity Pulse
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          13-week rolling cash-flow forecast and covenant health.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="W1 Ending Cash"
          value={weekOne ? fmt.compact(weekOne.ending_cash) : '—'}
          subtext={weekOne ? `Status: ${weekOne.liquidity_status}` : undefined}
          color={weekOne ? STATUS_BAR_COLORS[weekOne.liquidity_status] : undefined}
        />
        <KpiCard
          label="W13 Ending Cash"
          value={weekThirteen ? fmt.compact(weekThirteen.ending_cash) : '—'}
          subtext={
            weekThirteen ? `Status: ${weekThirteen.liquidity_status}` : undefined
          }
          color={
            weekThirteen
              ? STATUS_BAR_COLORS[weekThirteen.liquidity_status]
              : undefined
          }
        />
        <KpiCard
          label="13W Net Cash Flow"
          value={fmt.compact(totalNetCashFlow)}
          color={
            totalNetCashFlow >= 0 ? colors.status.green : colors.status.red
          }
        />
        <KpiCard
          label="Min Covenant Headroom"
          value={fmt.compact(minHeadroom)}
          subtext={
            fac ? `Covenant: ${fmt.currency(fac.minimum_cash_covenant)}` : undefined
          }
          color={minHeadroom > 0 ? colors.status.green : colors.status.red}
        />
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 dark:bg-dark-surface dark:border-dark-border">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
          13-Week Cash Horizon
        </h2>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.chart.gridLine}
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tickFormatter={(week: number) => `W${week}`}
              tick={{ fontSize: 12, fill: colors.text.secondary }}
            />
            <YAxis
              tickFormatter={(value: number) => fmt.compact(value)}
              tick={{ fontSize: 12, fill: colors.text.secondary }}
              width={72}
            />
            <Tooltip content={<WeekTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            {fac && (
              <ReferenceLine
                y={fac.minimum_cash_covenant}
                stroke={colors.status.red}
                strokeDasharray="6 4"
                strokeWidth={2}
                label={{
                  value: 'Min Covenant',
                  position: 'insideTopRight',
                  fill: colors.status.red,
                  fontSize: 11,
                }}
              />
            )}
            <Bar dataKey="ending_cash" radius={[4, 4, 0, 0]} maxBarSize={48}>
              {rows.map(row => (
                <Cell
                  key={String(row.week)}
                  fill={
                    STATUS_BAR_COLORS[row.liquidity_status] ?? colors.status.green
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2 rounded-xl bg-white shadow-sm border border-gray-100 p-5 space-y-5 dark:bg-dark-surface dark:border-dark-border">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Covenant and Facility
          </h2>

          {fac && (
            <>
              <CovenantGauge
                ratio={
                  fac.minimum_cash_covenant > 0
                    ? ((weekOne?.ending_cash ?? 0) / fac.minimum_cash_covenant) / 2
                    : 0
                }
                label="Covenant Coverage"
              />

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>Revolver Utilization</span>
                    <span>{fmt.pct(revolverUtilPct)}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 dark:bg-dark-surface-2 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(revolverUtilPct, 100)}%`,
                        backgroundColor:
                          revolverUtilPct >= 80
                            ? colors.status.red
                            : revolverUtilPct >= 60
                              ? colors.status.yellow
                              : colors.status.green,
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg p-3">
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                      Total Commitment
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fmt.currency(fac.total_commitment)}
                    </span>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg p-3">
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                      Credit Available
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fmt.currency(fac.available_credit)}
                    </span>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg p-3">
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                      Outstanding
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fmt.currency(fac.current_outstanding)}
                    </span>
                  </div>
                  <div className="bg-gray-50 dark:bg-dark-surface-2 rounded-lg p-3">
                    <span className="block text-xs text-gray-400 dark:text-gray-500">
                      Rate
                    </span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {fac.current_rate_pct.toFixed(2)}%
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 dark:bg-dark-surface dark:border-dark-border">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            Entity Health
          </h2>
          <div className="space-y-2.5">
            {entityHealth.length === 0 && (
              <p className="text-xs text-gray-400 italic">No AR aging data available.</p>
            )}
            {entityHealth.map(entity => {
              const dotColor =
                entity.over90Pct >= 0.2
                  ? colors.status.red
                  : entity.over90Pct >= 0.1
                    ? colors.status.yellow
                    : colors.status.green
              return (
                <div key={entity.name} className="flex items-center gap-2 text-sm">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: dotColor }}
                  />
                  <span className="truncate flex-1 text-gray-700 dark:text-gray-200">
                    {entity.name}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums whitespace-nowrap">
                    {fmt.compact(entity.total)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 dark:bg-dark-surface dark:border-dark-border">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            Priority Actions
          </h2>
          <div className="space-y-3">
            {priorityActions.length === 0 && (
              <p className="text-xs text-gray-400 italic">No open actions.</p>
            )}
            {priorityActions.map(action => (
              <div key={action.action_id} className="flex items-start gap-2">
                <span
                  className="mt-1.5 h-2 w-2 rounded-full shrink-0"
                  style={{
                    backgroundColor:
                      PRIORITY_COLORS[action.priority] ?? colors.text.muted,
                  }}
                />
                <div className="min-w-0">
                  <p className="text-sm text-gray-700 dark:text-gray-200 leading-tight truncate">
                    {action.description}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {action.owner} · {fmt.shortDate(action.due_date)} ·{' '}
                    {fmt.compact(action.estimated_cash_impact)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 dark:bg-dark-surface dark:border-dark-border">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
          Daily Cash Trend (Last 60 Days)
        </h2>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={recentBank} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
            <defs>
              <linearGradient id="cashflow-v2-teal-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.accent.teal} stopOpacity={0.3} />
                <stop offset="100%" stopColor={colors.accent.teal} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={colors.chart.gridLine}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={fmt.shortDate}
              tick={{ fontSize: 11, fill: colors.text.secondary }}
              minTickGap={40}
            />
            <YAxis
              tickFormatter={(value: number) => fmt.compact(value)}
              tick={{ fontSize: 11, fill: colors.text.secondary }}
              width={72}
            />
            <Tooltip
              formatter={(value: number) => [fmt.currency(value), 'Ledger Balance']}
              labelFormatter={fmt.shortDate}
              contentStyle={{
                borderRadius: 8,
                fontSize: 12,
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            />
            {fac && (
              <ReferenceLine
                y={fac.minimum_cash_covenant}
                stroke={colors.status.red}
                strokeDasharray="6 4"
                strokeWidth={1.5}
                label={{
                  value: 'Min Covenant',
                  position: 'insideTopRight',
                  fill: colors.status.red,
                  fontSize: 11,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="ledger_balance"
              stroke={colors.accent.teal}
              strokeWidth={2}
              fill="url(#cashflow-v2-teal-grad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
